import { useEffect, useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

interface Product {
  id: number;
  title: string;
  capacity: string;
  image_path: string | any;
  features: string[];
  price: number;
  category: string;
  description: string;
}

export default function Products() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [categories, setCategories] = useState<string[]>([]);

  // Fetch products from API
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const response = await fetch('/server/api/products.php');

        if (!response.ok) {
          throw new Error('Failed to fetch products');
        }

        const data = await response.json();
        const productsList = data.data?.products || data.data || [];

        setProducts(productsList);

        // Extract unique categories
        const uniqueCategories = [...new Set(productsList.map((p: Product) => p.category))] as string[];
        setCategories(uniqueCategories);
      } catch (err) {
        setError('Unable to load products. Showing offline mode.');
        console.error('Error fetching products:', err);
        // Fallback to hardcoded products if API fails
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const filteredProducts = selectedCategory === 'all'
    ? products
    : products.filter(p => p.category === selectedCategory);
  return (
    <div className="min-h-screen bg-white">
      <Navbar isScrolled={isScrolled} />
      <section className="relative h-80 sm:h-[400px] w-full flex items-center justify-center bg-white mt-20">
        <picture>
          <source media="(max-width: 768px)" srcSet="/assets/Products-mobile.png" />
          <img
            src="/assets/Products.png"
            alt="Our Products"
            className="absolute inset-0 w-full h-full object-contain p-4"
            style={{ margin: '0 auto' }}
          />
        </picture>
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/60 pointer-events-none"></div>
        <div className="relative z-10 w-full flex flex-col items-center justify-center text-center px-4">
          <div className="mb-4 inline-block">
            <div className="border-t-2 border-b-2 border-amber-400 py-2 px-4 sm:px-6">
              <span className="text-amber-300 text-xs sm:text-sm font-medium tracking-widest uppercase">
                Our Range
              </span>
            </div>
          </div>
          <h1 className="text-3xl sm:text-5xl md:text-6xl font-bold text-white mb-4 drop-shadow-lg leading-tight">
            Quality Products for Every Need
          </h1>
          <p className="text-sm sm:text-lg md:text-xl text-white/90 max-w-2xl mx-auto drop-shadow">
            Premium water tanks and milk cans manufactured with precision
          </p>
        </div>
      </section>
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
            <button
              onClick={() => setSelectedCategory('all')}
              className={`w-full sm:w-auto px-6 py-3 rounded-lg font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${selectedCategory === 'all'
                  ? 'bg-amber-600 text-white shadow-lg'
                  : 'bg-amber-100 text-amber-900 hover:bg-amber-200'
                }`}
            >
              All Products
            </button>
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`w-full sm:w-auto px-6 py-3 rounded-lg font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer capitalize ${selectedCategory === category
                    ? 'bg-amber-600 text-white shadow-lg'
                    : 'bg-amber-100 text-amber-900 hover:bg-amber-200'
                  }`}
              >
                {category.replace('-', ' ')}
              </button>
            ))}
          </div>

          {loading && (
            <div className="text-center py-20">
              <div className="text-amber-900 text-lg">Loading products...</div>
            </div>
          )}

          {error && (
            <div className="text-center py-8 px-6 bg-amber-50 rounded-lg border border-amber-200 mb-12">
              <p className="text-amber-900">{error}</p>
            </div>
          )}

          {!loading && filteredProducts.length === 0 && (
            <div className="text-center py-20">
              <div className="text-amber-900 text-lg">No products found in this category.</div>
            </div>
          )}

          {!loading && filteredProducts.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8" data-product-shop>
              {filteredProducts.map((product) => (
                <div
                  key={product.id}
                  className="group bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 border-2 border-amber-100 hover:border-amber-400 hover:-translate-y-2 cursor-pointer"
                >
                  {/* Image Container */}
                  <div className="relative h-56 sm:h-64 md:h-72 bg-gradient-to-br from-amber-50 to-white overflow-hidden">
                    <img
                      src={(() => {
                        const v: any = product.image_path;
                        const fallback = '/assets/500Lit.png';
                        if (!v) return fallback;
                        // If already an object with url/path/filename
                        if (typeof v === 'object') {
                          return v.url || v.path || (v.filename ? `/uploads/products/${v.filename}` : fallback);
                        }
                        // If string, try to parse JSON
                        if (typeof v === 'string') {
                          // JSON string stored
                          try {
                            const parsed = JSON.parse(v);
                            if (parsed && typeof parsed === 'object') {
                              return parsed.url || parsed.path || (parsed.filename ? `/uploads/products/${parsed.filename}` : fallback);
                            }
                          } catch (e) {
                            // not JSON
                          }

                          // If full URL or absolute path
                          if (v.startsWith('http') || v.startsWith('/')) return v;

                          // Otherwise treat as filename and build uploads path
                          return `/uploads/products/${v}`;
                        }

                        return fallback;
                      })()}
                      alt={product.title}
                      className="w-full h-full object-cover object-center p-6 sm:p-8 group-hover:scale-110 transition-transform duration-300"
                      onError={(e) => { (e.currentTarget as HTMLImageElement).src = '/assets/500Lit.png'; }}
                    />
                    {/* Overlay effect */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </div>
                  
                  {/* Content Container */}
                  <div className="p-4 sm:p-5">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <h3 className="text-lg sm:text-xl font-bold text-amber-900 group-hover:text-amber-700 transition-colors flex-1">{product.title}</h3>
                      <span className="px-2 py-1 bg-amber-100 text-amber-900 text-xs font-semibold rounded-full whitespace-nowrap flex-shrink-0">
                        {product.capacity}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">{product.description}</p>
                    <div className="flex flex-wrap gap-1 mb-4">
                      {product.features && Array.isArray(product.features) && product.features.slice(0, 2).map((feature, idx) => (
                        <span
                          key={idx}
                          className="px-2 py-1 bg-amber-50 text-amber-700 text-xs font-medium rounded border border-amber-200"
                        >
                          {feature}
                        </span>
                      ))}
                      {product.features && Array.isArray(product.features) && product.features.length > 2 && (
                        <span className="px-2 py-1 text-amber-700 text-xs font-medium">+{product.features.length - 2}</span>
                      )}
                    </div>
                    <div className="flex items-center justify-between pt-3 border-t border-amber-100">
                      <span className="text-sm sm:text-base font-bold text-amber-900">
                        {product.price > 0 ? `₹${product.price.toLocaleString()}` : 'Contact'}
                      </span>
                      <button className="px-3 py-2 bg-amber-600 hover:bg-amber-700 text-white text-xs sm:text-sm font-semibold rounded-lg transition-all duration-300 whitespace-nowrap cursor-pointer shadow-md hover:shadow-lg">
                        Enquire
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}